import React, { useContext } from 'react';
import styled from 'styled-components';
import { Text, Title, Icon, Card, Button } from '@bb/elements-web';
import { useBreakpoint } from 'hooks';
import { StyledPageContent, TitleCircle, PhoneBox } from '../StyledComponents';
import AnsweringServiceContext from '../AnsweringServiceContext';
import PhoneBoxPopulated from '../ManageListings/PhoneBoxPopulated';

/**
 * The page confirming to the user their subscription has been
 * created and displaying their Answering Service phone number.
 * The final page in the Answering Service subscription signup flow.
 * */
const SubscriptionSuccess: React.FC = () => {
  const { billingRedirection, viewingLevel, billingDetails } = useContext(
    AnsweringServiceContext,
  );
  const { sm } = useBreakpoint();

  const nextBillingCycleStartDate = () => {
    if (!billingDetails?.nextBillingCycleStartDate) return '-';

    // Remove timestamp
    const tempDate = new Date(
      `${billingDetails.nextBillingCycleStartDate.split('T')[0]}T00:00:00`,
    );

    return tempDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <StyledPageContent>
      <StyledCard dataCy="payment-confirmation-card">
        <StyledIconContainer>
          <Icon icon="checkCircleFilled" color="primary" size={66} />
        </StyledIconContainer>
        <StyledTitleContainer>
          <Title textAlign="center" level={2} dataCy="confirmation-title">
            {viewingLevel === 'user'
              ? 'Your concierge service is now live!'
              : `Your brokerage's concierge service is now live!`}
          </Title>
        </StyledTitleContainer>
        <StyledPhoneBox>
          <PhoneBoxPopulated />
        </StyledPhoneBox>
        <StyledConfirmationContainer $sm={sm}>
          <Text thickness="bold">How it works</Text>
          <StyledHowItWorksText color="grey">
            Add this number to your MLS listings or redirect any existing
            showing-related numbers to it. Our service will take all showing
            requests on your behalf.
          </StyledHowItWorksText>
          <Text thickness="bold" style={{ fontSize: 18 }}>
            Your Subscription
          </Text>
          <StyledSubscriptionDetailsContainer>
            <ServiceListingsContainer>
              <TitleCircle>
                <Icon
                  icon="liveConcierge"
                  size={26}
                  color="primary"
                  style={{ display: 'flex' }}
                />
              </TitleCircle>
              <TextContainer>
                <Text style={{ fontSize: 16 }}>Live Concierge</Text>
                <Text style={{ fontSize: 16 }} color="grey">
                  {`Next billing date: ${nextBillingCycleStartDate()}`}
                </Text>
              </TextContainer>
            </ServiceListingsContainer>
          </StyledSubscriptionDetailsContainer>
          <StyledButton
            type="primary"
            minWidth="auto"
            onClick={billingRedirection}
            dataCy="listings-continue-button"
          >
            Return to Subscriptions
          </StyledButton>
        </StyledConfirmationContainer>
      </StyledCard>
    </StyledPageContent>
  );
};

const StyledPhoneBox = styled.div`
  ${PhoneBox} {
    text-align: center;
  }
`;

const ServiceListingsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const StyledIconContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1em;
`;

const StyledTitleContainer = styled.div`
  margin: 2em 0;
  display: flex;
  align-items: center;
  text-align: center;
`;

const StyledButton = styled(Button)`
  align-self: center;
`;

const StyledCard = styled(Card)`
  padding: 10px;
  width: 550px;
`;

const StyledConfirmationContainer = styled.div<{ $sm: boolean }>`
  width: ${({ $sm }) => ($sm ? 'max(480px, 40%)' : '100%')};
  display: flex;
  flex-flow: column nowrap;
  margin-top: 24px;
`;

const StyledHowItWorksText = styled(Text)`
  font-size: 14px;
  line-height: 22px;
  margin-top: 4px;
  margin-bottom: 2em;
`;

const StyledSubscriptionDetailsContainer = styled.div`
  margin-top: 0.5em;
  margin-bottom: 2em;
  border: 1px solid grey;
  width: 473px;
  height: 96px;
  border-radius: 6px;
  gap: 16px;
  padding: 24px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export default SubscriptionSuccess;
